import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useT } from "@/lib/i18n";
import { storyCardsFor, type StoryPackPrefix } from "@/data/storyMedia";
import { getStoredAdEventType } from "@/lib/quizEventType";
import { BEAT_HOLD_MS, BEAT_TRANSITION } from "../beats";
import { BLEED } from "../layout";
import ShrinkingBeat from "../ShrinkingBeat";
import type { IntroScreenDef, ScreenComponentProps } from "../types";

/**
 * First screen. A slow conveyor of real nights, then two text beats — "your
 * people are out there", then the promise — and one CTA.
 *
 * Them first, product second: nothing here explains what Outclose is yet.
 * The prints do that work, and the caption on each is a handwritten note from
 * an actual dinner.
 */

/**
 * The belt itself: timing and direction, which don't depend on who is looking.
 * Delays are negative so the stack is already mid-cycle on first paint rather
 * than assembling itself while the user watches, and the exit direction
 * alternates because a belt that always throws left reads mechanical.
 */
const SLOTS = [
  { stamp: "08 06 '26", delay: "-7.4s", dir: "left" },
  { stamp: "07 24 '26", delay: "-4.4s", dir: "right" },
  { stamp: "07 31 '26", delay: "-1.4s", dir: "left" },
  { stamp: "06 28 '26", delay: "1.6s",  dir: "right" },
] as const;

/**
 * Captions are chosen per photo, not per slot.
 *
 * A caption is a handwritten note on someone's photo, so it has to agree with
 * what the photo shows and with whose night it was. Positional captions did
 * neither once the deck started varying: "sunday coffee" sat over a candlelit
 * dinner, and dropping a repeated table (see `distinctTables`) would slide
 * "pride pregame" onto a photo with no flag in it.
 *
 * Two cards say something specific enough to name — the one with a flag, the
 * one in a cafe. Everything else takes the next unused line from its own
 * pack's pool, so "boys' night" only ever lands on a men's card and "sapphic
 * supper" only on a sapphic one, however the packs alternate.
 */
const CARD_CAPTION: Record<string, string> = {
  pride: "nyc",
  cafe: "paris",
};

const CAPTION_POOL: Record<StoryPackPrefix, readonly string[]> = {
  "": ["montreal", "silverLake", "london", "chicago"],
  wm: ["montreal", "silverLake", "london", "chicago"],
  m: ["berlin", "silverLake", "london", "chicago"],
  mm: ["berlin", "silverLake", "london", "chicago"],
};

// Resting positions for reduced motion: the same three stack slots, held
// still. The fourth card is dropped — with nothing turning, it would just sit
// invisibly off-screen.
const STATIC_SLOTS = [
  "translate(26px,0px) rotate(4.5deg) scale(.9)",
  "translate(6px,12px) rotate(-5deg) scale(.95)",
  "translate(38px,24px) rotate(-1deg) scale(1)",
];


export default function IntroScreen({
  def,
  rt,
}: ScreenComponentProps<IntroScreenDef>) {
  const { t } = useT();
  const reducedMotion = useReducedMotion();
  // The belt is four large shadowed prints on two infinite 12s loops — by some
  // distance the most expensive thing in the flow. It used to be held on
  // screen after the flow had moved on, because the shell cross-faded screens
  // and `mode="wait"` kept this one mounted and animating behind the next one;
  // the cards were dropped explicitly via `useIsPresent` to stop that. The
  // shell now swaps screens outright, so unmounting does it.
  //
  // The prints follow the ad the visitor arrived on: `a=` picks whose nights
  // these are, `et=` decides whether the deck opens on a cafe or a table.
  // Someone who clicked a sapphic coffee ad and lands on a stack of men at
  // candlelit dinners has been shown the wrong product before reading a word.
  const eventType = getStoredAdEventType();
  const prints = useMemo(() => {
    // One card per table: two prints of the same night, captioned as two
    // cities, is the fastest way to tell a reader these are not real evenings.
    const cards = storyCardsFor(rt.audience, eventType, SLOTS.length, null, {
      distinctTables: true,
    });
    const used = new Set<string>();
    return SLOTS.map((slot, i) => {
      const card = cards[i];
      const specific = CARD_CAPTION[card.name];
      const pool = CAPTION_POOL[card.prefix] ?? CAPTION_POOL[""];
      const captionKey =
        specific && !used.has(specific)
          ? specific
          : (pool.find((key) => !used.has(key)) ?? pool[pool.length - 1]);
      used.add(captionKey);
      return { ...slot, src: card.shot.img, captionKey };
    });
  }, [rt.audience, eventType]);
  // One-way: the headline holds alone, then shrinks to reveal the promise and
  // both stay. Nothing loops — with both lines on screen there is nothing left
  // to cycle, and re-running it under a reader is just noise.
  const [opened, setOpened] = useState(Boolean(reducedMotion));

  useEffect(() => {
    if (reducedMotion) return;
    const id = setTimeout(() => setOpened(true), BEAT_HOLD_MS);
    return () => clearTimeout(id);
  }, [reducedMotion]);

  // Reduced motion keeps the stack, loses the belt: three prints at rest.
  const cards = reducedMotion ? prints.slice(0, 3) : prints;

  return (
    <div className="flex-1 flex flex-col">
      {/* The wordmark is the shell's — it has to outlive this screen to fly
          into the top bar when the flow moves on. See OnboardingShell. */}
      {/* The conveyor. The outer box is full-bleed and clipped so a card
          leaving the stack flies off the edge rather than widening the page;
          the inner box is the stack's own footprint.

          Bleeds via `BLEED`, which cancels the shell's `GUTTER`,
          rather than with `w-screen`. `100vw` is measured against the viewport
          *including* the vertical scrollbar, so on any browser with classic
          rather than overlay scrollbars it is wider than the space available
          and pushes the page sideways — and being viewport-sized, it overflows
          its padded parent by the gutter whatever the scrollbar does.

          The vertical padding is what keeps the clip horizontal-only in
          practice. `overflow-hidden` clips both axes, but it clips at the
          *padding* box — and the stack needs room the 300px footprint doesn't
          have: the exit keyframe drops the card 74px before it leaves, and the
          shadow hangs below that. Without the padding both were sliced flat
          against the container edge. `-mb-24` takes the room back out of the
          layout so nothing below moves. */}
      <div className={`relative ${BLEED} self-stretch overflow-hidden mt-4 pt-4 pb-36 -mb-36`}>
        <div className="relative w-[282px] h-[300px] mx-auto">
          {cards.map((print, i) => (
            <div
              key={print.src}
              className={`absolute top-0 left-0 w-[206px] ${
                reducedMotion
                  ? ""
                  : print.dir === "left"
                    ? "animate-convey-left"
                    : "animate-convey-right"
              }`}
              style={
                reducedMotion
                  ? { transform: STATIC_SLOTS[i], zIndex: i + 1 }
                  : {
                      animationDelay: print.delay,
                    }
              }
            >
              {/* Two shadows, because one cast can't serve both grounds. The
                  original 56px black at .6 was drawn for the dark canvas this
                  flow used to force; on white it reads as grime under the
                  print rather than depth. Light gets a short, faint cast that
                  still lifts the card off the page, and dark is pulled in far
                  enough to clear the clip box above without needing another
                  40px of padding to hide in. */}
              <div className="bg-[#F6F2E9] rounded-md p-2.5 pb-8 shadow-[0_8px_20px_rgba(0,0,0,.10)] dark:shadow-[0_16px_36px_rgba(0,0,0,.55)]">
                <div className="relative overflow-hidden">
                  <img
                    src={print.src}
                    alt=""
                    aria-hidden
                    className="block w-full h-[196px] object-cover"
                    style={{
                      filter:
                        "sepia(.28) saturate(.82) contrast(1.06) brightness(1.03)",
                    }}
                  />
                  {/* Vignette + a burned-in date, the way a film print carries it. */}
                  <div className="absolute inset-0 shadow-[inset_0_0_44px_rgba(62,38,12,.35)]" />
                  <div
                    className="absolute right-2 bottom-1.5 font-mono font-semibold text-[11px] text-[#FFB03A]"
                    style={{ textShadow: "0 0 6px rgba(255,140,40,.9)" }}
                  >
                    {print.stamp}
                  </div>
                </div>
                <div
                  className="text-center mt-1 text-[#4A4034] font-medium"
                  style={{ fontFamily: "Caveat, cursive", fontSize: 18 }}
                >
                  {t(`${def.copyKey}.captions.${print.captionKey}`)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Two beats, same mechanic as the handoffs: the headline lands, then
          shrinks and dims in place to reveal the promise underneath. It is
          never unmounted — "your people are out there" is the line the whole
          screen rests on, and it should still be readable while the promise
          explains it. */}
      <div className="mt-5 pb-4">
        <ShrinkingBeat shrunk={opened}>
          <h1 className="font-display font-semibold text-[34px] leading-[1.16] tracking-tight text-neutral-900 dark:text-neutral-50">
            {t(`${def.copyKey}.headline`)}
          </h1>
        </ShrinkingBeat>
        <AnimatePresence>
          {opened && (
            <motion.p
              key="promise"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={BEAT_TRANSITION}
              className="text-[15px] leading-relaxed text-neutral-600 dark:text-neutral-400 max-w-[330px] mt-4"
            >
              {t(`${def.copyKey}.body`)}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

    </div>
  );
}
